import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getHealthLang, makeHT } from "@/lib/healthI18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, ShieldCheck, Loader2 } from "lucide-react";

export default function HealthLogin() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const ht = makeHT(getHealthLang());
  const [email, setEmail] = useState("dr.mueller@neunexus.health");
  const [password, setPassword] = useState("neunexus2026");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/health");
  };

  return (
    <div className="min-h-screen flex bg-[#050C11] text-slate-100">
      <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-slate-800 p-12 relative overflow-hidden">
        <div className="absolute inset-0 ngx-grid-overlay opacity-30" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-teal-600 flex items-center justify-center">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">NeuNexus</div>
            <div className="text-xs uppercase tracking-widest text-teal-400/80">Health Intelligence</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">{ht("tagline")}</h1>
          <div className="mt-8 flex flex-col gap-2 text-xs font-mono uppercase tracking-widest">
            <span className="flex items-center gap-2 text-amber-400"><span className="h-2 w-2 rounded-full bg-amber-500 ngx-pulse" /> {ht("demoEnv")}</span>
            <span className="text-slate-500">{ht("syntheticOnly")} · {ht("notClinical")}</span>
          </div>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-widest text-slate-600">KI.WI · Region Hannover</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6 text-teal-400 text-xs font-semibold uppercase tracking-wider">
            <ShieldCheck className="h-4 w-4" /> {ht("professionalAuth")}
          </div>
          <h2 className="font-display text-2xl font-bold mb-6">{ht("signIn")}</h2>
          <form onSubmit={submit} className="space-y-4" data-testid="health-login-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">{ht("email")}</Label>
              <Input data-testid="health-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-12 bg-[#0A1620] border-slate-700 font-mono text-slate-100 focus:ring-2 focus:ring-teal-500" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">{ht("password")}</Label>
              <Input data-testid="health-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-12 bg-[#0A1620] border-slate-700 font-mono text-slate-100 focus:ring-2 focus:ring-teal-500" required />
            </div>
            {error && <p className="text-sm text-red-400 font-mono" data-testid="health-login-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="health-login-submit"
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : ht("enter")}
            </Button>
          </form>
          <p className="mt-6 text-xs text-slate-500 font-mono">Demo: dr.mueller@neunexus.health · neunexus2026</p>
        </div>
      </div>
    </div>
  );
}
